import * as React from "react";
import { XIcon, type LucideIcon } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShareUsersForm } from "@/components/share/components/ShareUsersForm";
import { ShareUserRow } from "@/components/share/components/ShareUserRow";
import { ShareGeneralAccess } from "@/components/share/components/ShareGeneralAccess";
import type { ShareAccess, SharePermission, ShareRole } from "@/components/share/types";

interface Props<P extends SharePermission> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: React.ReactNode;
  isReadOnly: boolean;
  permissions: P[] | undefined;
  access: ShareAccess;
  shareSuccessMessage: string;
  onShare: (userIds: string[], role: ShareRole) => Promise<void>;
  onRemovePermission: (permission: P) => Promise<void>;
  canRemovePermission: (permission: P) => boolean;
  onAccessChange: (access: ShareAccess) => Promise<void>;
  RemoveIcon?: LucideIcon;
}

export const ShareDialog = <P extends SharePermission>({
  isOpen,
  setIsOpen,
  title,
  isReadOnly,
  permissions,
  access,
  shareSuccessMessage,
  onShare,
  onRemovePermission,
  canRemovePermission,
  onAccessChange,
  RemoveIcon = XIcon,
}: Props<P>) => {
  const [isNewUserSelected, setIsNewUserSelected] = React.useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="flex min-h-[400px] w-full max-w-[40vw] flex-grow flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ShareUsersForm
          isReadOnly={isReadOnly}
          isNewUserSelected={isNewUserSelected}
          setIsNewUserSelected={setIsNewUserSelected}
          onShare={onShare}
          successMessage={shareSuccessMessage}
        />

        {!isNewUserSelected && (
          <>
            {!!permissions && (
              <>
                <div className="mt-3">
                  <h4 className="mb-2 text-sm font-semibold">People with access</h4>
                  {permissions.map(permission => (
                    <ShareUserRow
                      key={permission.id}
                      permission={permission}
                      canRemove={!isReadOnly && canRemovePermission(permission)}
                      onRemove={() => onRemovePermission(permission)}
                      RemoveIcon={RemoveIcon}
                    />
                  ))}
                </div>
                <ShareGeneralAccess isReadOnly={isReadOnly} access={access} onAccessChange={onAccessChange} />
              </>
            )}
            <DialogFooter>
              <Button onClick={() => setIsOpen(false)} variant="default">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
